import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaGift,
  FaTimes,
  FaApple,
  FaGooglePlay,
  FaCopy,
  FaQrcode,
} from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { QRCodeCanvas as QRCode } from "qrcode.react";
import SiteBackdrop from "../components/site/SiteBackdrop";
import { Button, Card, Field, Input, Spinner } from "../ui";
import {
  getGiftCodeByLink,
  redeemGiftCode,
  revealGiftCode,
} from "../api/gift-code/index";

const GiftCode = () => {
  const location = useLocation();
  const code = location.pathname.split("/").pop();
  const [isRevealed, setIsRevealed] = useState(false);
  const [showRedeemDialog, setShowRedeemDialog] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    number: "",
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["giftCode", code],
    queryFn: () => getGiftCodeByLink(code),
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const revealMutation = useMutation({
    mutationFn: (userData) => revealGiftCode(code, userData),
    onSuccess: (data) => {
      setIsRevealed(true);
    },
  });

  const redeemMutation = useMutation({
    mutationFn: () => redeemGiftCode(giftCodeData?.code),
    onSuccess: () => {
      setShowRedeemDialog(false);
    },
  });

  const handleReveal = () => {
    if (giftCodeData?.usedBy?.isRequired) {
      setShowRedeemDialog(true);
    } else {
      revealMutation.mutate();
    }
  };

  const handleRedeem = () => {
    redeemMutation.mutate();
  };

  const handleRedeemSubmit = () => {
    if (!userData.name || !userData.email) {
      return;
    }
    revealMutation.mutate(userData);
    setShowRedeemDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => {
    if (data?.data?.isRevealed) {
      setIsRevealed(true);
    }
  }, [data]);

  if (isLoading) {
    return (
      <div
        dir="rtl"
        role="status"
        className="relative isolate flex justify-center items-center min-h-screen overflow-hidden bg-header font-sans text-primary"
      >
        <SiteBackdrop />
        <Spinner size={48} />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        dir="rtl"
        className="relative isolate flex justify-center items-center min-h-screen px-4 overflow-hidden bg-header font-sans text-white"
      >
        <SiteBackdrop />
        <Card tone="solid" className="w-full max-w-sm border-danger/40" role="alert">
          <p className="font-bold text-danger">خطأ</p>
          <p className="text-white/80 mt-1">
            {error.message || "رمز الهدية هذا غير موجود أو انتهت صلاحيته."}
          </p>
        </Card>
      </div>
    );
  }

  const giftCodeData = data?.data;
  const currentCode = revealMutation.data?.data?.code || giftCodeData.code;

  const getRedeemUrl = (type, code) => {
    if (type && type.includes("AppStore")) {
      return `https://apps.apple.com/redeem?code=${code}`;
    } else if (type && type.includes("GooglePlay")) {
      return `https://play.google.com/redeem?code=${code}`;
    }
    return null;
  };

  const getTypeDescription = (type) => {
    switch (type) {
      case "AppStorePaidVersion":
        return "النسخة المدفوعة من متجر آب ستور";
      case "GooglePlayPaidVersion":
        return "النسخة المدفوعة من متجر جوجل بلاي";
      case "AppStorePremium1Month":
        return "اشتراك بريميوم لمدة شهر من متجر آب ستور";
      case "GooglePlayPremium1Month":
        return "اشتراك بريميوم لمدة شهر من متجر جوجل بلاي";
      case "AppStorePremium1Year":
        return "اشتراك بريميوم لمدة سنة من متجر آب ستور";
      case "GooglePlayPremium1Year":
        return "اشتراك بريميوم لمدة سنة من متجر جوجل بلاي";
      default:
        return "هدية خاصة";
    }
  };

  const getTypeIcon = (type) => {
    if (type && type.includes("AppStore")) {
      return <FaApple className="mr-2" />;
    } else if (type && type.includes("GooglePlay")) {
      return <FaGooglePlay className="mr-2" />;
    }
    return null;
  };

  const getTypeBadgeStyle = (type) => {
    if (type && type.includes("AppStore")) {
      return "bg-white/10 text-white border border-white/15"; // Apple
    } else if (type && type.includes("GooglePlay")) {
      return "bg-white/10 text-white border border-white/15"; // Google Play
    }
    return "bg-primary/15 text-primary border border-primary/30"; // Default style
  };
  // Add this function to create an Apple-style scannable promo code
  const renderApplePromoCode = (code) => {
    // The ratio should be 3:1 (width:height)
    const boxHeight = 70; // in pixels
    const boxWidth = boxHeight * 3;
    const borderThickness = Math.round(boxHeight * 0.045); // factor 0.045
    const fontSize = Math.round(boxHeight * 0.26); // factor 0.34

    return (
      <div
        dir="ltr"
        className="mx-auto my-4"
        style={{
          width: boxWidth + "px",
          height: boxHeight + "px",
          border: `${borderThickness}px solid #000`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fff",
        }}
      >
        <p
          style={{
            fontFamily: "'Scancardium', 'Courier', monospace",
            color: "#000",
            fontSize: fontSize + "px",
            lineHeight: "1",
            margin: 0,
            padding: 0,
            letterSpacing: "0.5px",
          }}
        >
          {code}
        </p>
      </div>
    );
  };

  return (
    <div
      dir="rtl"
      className="relative isolate flex justify-center items-center min-h-screen py-10 px-4 overflow-hidden bg-header font-sans text-white"
    >
      <SiteBackdrop />
      <div className="w-full max-w-md">
        <Card
          tone="solid"
          pad="none"
          className="overflow-hidden shadow-2xl shadow-black/30"
        >
          {/* Header */}
          <div className="p-6 text-center relative border-b border-white/10 bg-white/[0.04]">
            <div className="w-12 h-12 rounded-2xl bg-primary/15 text-primary flex items-center justify-center mx-auto mb-3">
              <FaGift className="text-xl" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-extrabold">
              {giftCodeData.title || "هدية خاصة لك"}
            </h1>
          </div>

          {/* Card Content */}
          <div className="p-6">
            <div className="text-center mb-6">
              <p className="text-lg text-white/80 font-medium">
                {giftCodeData.info || "اكشط البطاقة لكشف رمز الهدية الخاص بك!"}
              </p>
            </div>

            {/* Gift Type Badge */}
            {giftCodeData.type && (
              <div className="text-center mb-4">
                <span
                  className={`${getTypeBadgeStyle(
                    giftCodeData.type
                  )} text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center`}
                >
                  {getTypeDescription(giftCodeData.type)}
                  {getTypeIcon(giftCodeData.type)}
                </span>
              </div>
            )}

            <div className="flex justify-center gap-2 mb-6">
              {giftCodeData.isExpired && (
                <span className="bg-danger/10 text-danger text-xs font-semibold px-3 py-1 rounded-full border border-danger/30">
                  منتهي الصلاحية
                </span>
              )}
              {!giftCodeData.isActive && (
                <span className="bg-white/10 text-white/70 text-xs font-semibold px-3 py-1 rounded-full border border-white/15">
                  غير نشط
                </span>
              )}
              {giftCodeData.expirationDate && !giftCodeData.isExpired && (
                <span className="bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full border border-primary/30">
                  ينتهي في{" "}
                  {new Date(giftCodeData.expirationDate).toLocaleDateString(
                    "en-UK"
                  )}{" "}
                  -{" "}
                  {new Date(giftCodeData.expirationDate).toLocaleDateString(
                    "ar-SA",
                    { numberingSystem: "latn" }
                  )}
                </span>
              )}
            </div>

            {/* Scratch Card Area */}
            <div
              className={`relative mx-auto w-full rounded-2xl flex justify-center items-center mb-6 cursor-pointer overflow-hidden transition-all duration-500 ${
                isRevealed
                  ? "bg-white/[0.05] border-2 border-primary/40"
                  : "bg-mainButton hover:bg-mainButtonShadow shadow-[0_10px_24px_-10px_rgb(var(--c-mainButton)/0.8)]"
              }`}
              style={{ height: "180px" }}
              onClick={!isRevealed ? handleReveal : undefined}
            >
              {revealMutation.isPending ? (
                <Spinner size={40} className="text-white" />
              ) : isRevealed ? (
                <div className="text-center w-full px-4">
                  <h2 className="text-lg font-bold mb-3 text-white">
                    الرمز الخاص بك
                  </h2>
                  {giftCodeData.type &&
                  giftCodeData.type.includes("AppStore") ? (
                    <div
                      onClick={() => copyToClipboard(currentCode)}
                      className="cursor-pointer flex "
                    >
                      <div className="flex items-center justify-center flex-col">
                        <FaCopy className="text-white/60 hover:text-primary cursor-pointer" />
                        {copied ? (
                          <span className="text-success text-sm ml-2">
                            تم النسخ!
                          </span>
                        ) : null}
                      </div>
                      {renderApplePromoCode(currentCode)}
                    </div>
                  ) : (
                    <div
                      className="bg-white/[0.07] p-4 rounded-xl border border-white/15 mx-auto flex items-center justify-between relative cursor-pointer hover:border-primary/40"
                      onClick={() => copyToClipboard(currentCode)}
                    >
                      <p
                        dir="ltr"
                        className="text-lg font-mono font-bold tracking-wider text-primary select-all px-2 py-1 rounded"
                      >
                        {currentCode}
                      </p>
                      <div className="flex items-center">
                        {copied ? (
                          <span className="text-success text-sm ml-2">
                            تم النسخ!
                          </span>
                        ) : null}
                        <FaCopy className="text-white/60 hover:text-primary cursor-pointer" />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center relative w-full h-full">
                  <div
                    className="absolute inset-0 bg-repeat"
                    style={{
                      backgroundImage:
                        "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiPjxjaXJjbGUgY3g9IjQiIGN5PSI0IiByPSIxIiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMylcIj48L2NpcmNsZT48L3N2Zz4=')",
                      opacity: 0.1,
                    }}
                  ></div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                    <div className="w-16 h-16 mx-auto mb-3 flex justify-center items-center bg-white/30 rounded-full">
                      <FaGift className="text-4xl text-white" aria-hidden="true" />
                    </div>
                    <p className="text-white font-bold text-lg drop-shadow">
                      إضغط هنا للكشف عن الرمز
                    </p>
                    <p className="text-white/85 text-sm drop-shadow mt-2 px-4">
                      تنبيه: عند الكشف عن الرمز، لا يمكن استخدامه مرة أخرى
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* QR Code Button & Redeem Button */}
            {isRevealed && (
              <div className="flex gap-2 mb-4">
                {getRedeemUrl(giftCodeData.type, currentCode) && (
                  <Button
                    variant="secondary"
                    size="lg"
                    className="flex-1"
                    onClick={() => setShowQRCode(true)}
                    iconEnd={<FaQrcode aria-hidden="true" />}
                  >
                    مسح رمز QR
                  </Button>
                )}
                {!redeemMutation.isSuccess && (
                  <Button
                    size="lg"
                    className="flex-1"
                    onClick={handleRedeem}
                    disabled={
                      !giftCodeData.isActive ||
                      giftCodeData.isExpired ||
                      redeemMutation.isSuccess
                    }
                  >
                    {redeemMutation.isPending ? <Spinner /> : "تأكيد الاستخدام"}
                  </Button>
                )}
              </div>
            )}

            {/* Success Message */}
            {redeemMutation.isSuccess && (
              <div className="bg-success/10 border border-success/40 text-success px-6 py-5 rounded-2xl text-center">
                <FaCheckCircle
                  className="text-success text-3xl mx-auto mb-3"
                  aria-hidden="true"
                />
                <p className="font-bold text-lg">تم استخدام الرمز بنجاح!</p>
                <p className="mt-1 text-white/80">شكرًا لك.</p>
              </div>
            )}

            {/* Error Message */}
            {redeemMutation.isError && (
              <div
                role="alert"
                className="bg-danger/10 border border-danger/40 text-danger px-6 py-5 rounded-2xl text-center"
              >
                <p className="font-bold">فشل في استخدام الرمز</p>
                <p className="mt-1 text-white/80">
                  {redeemMutation.error?.message || "يرجى المحاولة مرة أخرى."}
                </p>
              </div>
            )}

            {/* Instructions (show when revealed) */}
            {isRevealed && getRedeemUrl(giftCodeData.type, currentCode) && (
              <div className="mt-4 p-4 bg-white/[0.05] rounded-xl border border-white/10 text-sm">
                <h3 className="font-bold text-white mb-2">
                  كيفية استخدام الرمز:
                </h3>

                <ol className="list-decimal list-inside text-white/75 mb-3">
                  {giftCodeData.type &&
                  giftCodeData.type.includes("AppStore") ? (
                    <>
                      <li>امسح رمز QR</li>
                    </>
                  ) : giftCodeData.type &&
                    giftCodeData.type.includes("GooglePlay") ? (
                    <>
                      <li>امسح رمز QR</li>
                    </>
                  ) : (
                    <li>استخدم الرمز داخل التطبيق</li>
                  )}
                </ol>
                <h3 className="font-bold text-white mb-2">أو</h3>
                <ol className="list-decimal list-inside text-white/75">
                  {giftCodeData.type &&
                  giftCodeData.type.includes("AppStore") ? (
                    <>
                      <li className="mb-1">افتح تطبيق App Store</li>
                      <li className="mb-1">اضغط على صورة ملفك الشخصي</li>
                      <li className="mb-1">
                        اضغط على "استرداد بطاقة هدية أو رمز"
                      </li>
                      <li>أدخل الرمز</li>
                    </>
                  ) : giftCodeData.type &&
                    giftCodeData.type.includes("GooglePlay") ? (
                    <>
                      <li className="mb-1">افتح تطبيق Google Play</li>
                      <li className="mb-1">اضغط على أيقونة القائمة</li>
                      <li className="mb-1">اختر "استبدال"</li>
                      <li>أدخل الرمز</li>
                    </>
                  ) : (
                    <li>استخدم الرمز داخل التطبيق</li>
                  )}
                </ol>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Redeem Dialog */}
      {showRedeemDialog && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-40"></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="gift-redeem-title"
              className="bg-header text-white border border-white/10 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="p-5 border-b border-white/10 flex justify-between items-center gap-4">
                <h2 id="gift-redeem-title" className="text-xl font-extrabold">
                  أكمل عملية الكشف
                </h2>
                <button
                  onClick={() => setShowRedeemDialog(false)}
                  className="shrink-0 p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10"
                  aria-label="إغلاق"
                >
                  <FaTimes aria-hidden="true" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <Field
                  label={
                    <>
                      الاسم <span className="text-danger">*</span>
                    </>
                  }
                  htmlFor="gift-name"
                >
                  <Input
                    type="text"
                    id="gift-name"
                    name="name"
                    autoComplete="name"
                    value={userData.name}
                    onChange={handleInputChange}
                    required
                  />
                </Field>

                <Field
                  label={
                    <>
                      البريد الإلكتروني <span className="text-danger">*</span>
                    </>
                  }
                  htmlFor="gift-email"
                >
                  <Input
                    ltr
                    type="email"
                    id="gift-email"
                    name="email"
                    autoComplete="email"
                    value={userData.email}
                    onChange={handleInputChange}
                    required
                  />
                </Field>

                <Field label="رقم الهاتف (اختياري)" htmlFor="gift-number">
                  <Input
                    ltr
                    type="text"
                    id="gift-number"
                    name="number"
                    autoComplete="tel"
                    value={userData.number}
                    onChange={handleInputChange}
                  />
                </Field>
              </div>

              <div className="p-5 border-t border-white/10 flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowRedeemDialog(false)}
                >
                  إلغاء
                </Button>
                <Button
                  onClick={handleRedeemSubmit}
                  disabled={
                    revealMutation.isPending ||
                    !userData.name ||
                    !userData.email
                  }
                >
                  {revealMutation.isPending ? "جارِ المعالجة..." : "كشف الرمز"}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* QR Code Dialog */}
      {showQRCode && isRevealed && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-40"></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="gift-qr-title"
              className="bg-header text-white border border-white/10 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="p-5 border-b border-white/10 flex justify-between items-center gap-4">
                <h2 id="gift-qr-title" className="text-xl font-extrabold">
                  امسح رمز QR للاستخدام المباشر
                </h2>
                <button
                  onClick={() => setShowQRCode(false)}
                  className="shrink-0 p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10"
                  aria-label="إغلاق"
                >
                  <FaTimes aria-hidden="true" />
                </button>
              </div>

              <div className="p-6 flex flex-col items-center">
                <div className="bg-white p-4 rounded-2xl mb-4">
                  <QRCode
                    value={getRedeemUrl(giftCodeData.type, currentCode)}
                    size={200}
                    renderAs="svg"
                    includeMargin={true}
                    level="H"
                  />
                </div>
                <p className="text-center text-white/80 mb-3">
                  امسح رمز QR باستخدام كاميرا هاتفك أو تطبيق{" "}
                  {giftCodeData.type?.includes("AppStore")
                    ? "App Store"
                    : "Google Play"}
                </p>
                <p
                  dir="ltr"
                  className="text-center text-sm font-mono font-bold text-primary bg-white/[0.07] border border-white/10 p-2 rounded-lg w-full"
                >
                  {currentCode}
                </p>
              </div>

              <div className="p-4 border-t border-white/10 flex justify-end">
                <Button variant="secondary" onClick={() => setShowQRCode(false)}>
                  إغلاق
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GiftCode;
